export class AudioSystem {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private riverGain: GainNode | null = null;
  private riverSource: AudioBufferSourceNode | null = null;
  enabled = true;

  async initialize(): Promise<void> {
    if (!this.enabled) return;
    if (!this.context) {
      this.context = new AudioContext();
      this.master = this.context.createGain();
      this.master.gain.value = 0.36;
      this.master.connect(this.context.destination);
      this.startRiver();
    }
    if (this.context.state === 'suspended') await this.context.resume();
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (this.master) this.master.gain.value = enabled ? 0.36 : 0;
  }

  private startRiver(): void {
    if (!this.context || !this.master || this.riverSource) return;
    const buffer = this.context.createBuffer(1, this.context.sampleRate * 2, this.context.sampleRate);
    const samples = buffer.getChannelData(0);
    let last = 0;
    for (let i = 0; i < samples.length; i += 1) {
      const white = Math.random() * 2 - 1;
      last = last * 0.92 + white * 0.08;
      samples[i] = last * 0.55;
    }
    const source = this.context.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    const filter = this.context.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 1100;
    this.riverGain = this.context.createGain();
    this.riverGain.gain.value = 0.12;
    source.connect(filter).connect(this.riverGain).connect(this.master);
    source.start();
    this.riverSource = source;
  }

  setTension(stamina: number): void {
    if (this.riverGain && this.context) {
      this.riverGain.gain.setTargetAtTime(0.08 + (1 - stamina) * 0.22, this.context.currentTime, 0.2);
    }
  }

  tone(frequency: number, duration = 0.12, type: OscillatorType = 'sine', volume = 0.16): void {
    if (!this.enabled || !this.context || !this.master) return;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = type;
    oscillator.frequency.value = frequency;
    gain.gain.setValueAtTime(volume, this.context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, this.context.currentTime + duration);
    oscillator.connect(gain).connect(this.master);
    oscillator.start();
    oscillator.stop(this.context.currentTime + duration);
  }

  splash(): void {
    this.tone(140, 0.22, 'triangle', 0.25);
    setTimeout(() => this.tone(90, 0.28, 'sine', 0.18), 60);
  }

  throwWhoosh(): void {
    this.tone(380, 0.16, 'sawtooth', 0.08);
  }

  click(): void {
    this.tone(520, 0.08, 'square', 0.1);
  }

  alert(): void {
    this.tone(740, 0.1, 'square', 0.12);
  }

  success(): void {
    [523, 659, 784, 1047].forEach((frequency, index) => {
      setTimeout(() => this.tone(frequency, 0.34, 'triangle', 0.18), index * 95);
    });
  }

  failure(): void {
    [330, 262, 196, 131].forEach((frequency, index) => {
      setTimeout(() => this.tone(frequency, 0.34, 'sawtooth', 0.1), index * 130);
    });
  }

  siren(): void {
    [680, 480, 680, 480].forEach((frequency, index) => {
      setTimeout(() => this.tone(frequency, 0.28, 'sine', 0.09), index * 230);
    });
  }

  dispose(): void {
    this.riverSource?.stop();
    this.context?.close();
    this.context = null;
    this.master = null;
    this.riverSource = null;
  }
}
